import { useCallback, useEffect, useMemo, useRef } from 'react';
import type React from 'react';
import {
  type BodySelectionBookmark,
  captureBodySelectionBookmark,
  isIframeChromeRefreshActive,
  restoreBodySelectionBookmark,
  scrollIframeCaretIntoHostView,
} from '../utils/dom';
import { getIframeDocumentContentHeightPx } from '../utils/iframe';

const MAX_UNDO_STEPS = 50;

interface HistoryEntry {
  html: string;
  sel: BodySelectionBookmark | null;
}

export interface IframeUndoHistoryApi {
  /** Call immediately before a programmatic DOM change (table rows, etc.). */
  recordBeforeMutate: () => void;
  withProgrammatic: (fn: () => void) => void;
  tryUndo: () => boolean;
  tryRedo: () => boolean;
  /** After native `execCommand('undo'|'redo')`, realign baseline with the iframe body. */
  syncBaselineFromDom: () => void;
  /** True if the user has made any edits since the editor loaded. */
  isDirty: () => boolean;
}

/**
 * Single undo/redo stack for the iframe body: `beforeinput` + IME + programmatic edits
 * share one history. Each step stores HTML + a selection bookmark so undo/redo restores the caret.
 */
export function useIframeUndoHistory(
  iframeRef: React.RefObject<HTMLIFrameElement>,
  reportContentKey: string,
  syncState: () => void,
): IframeUndoHistoryApi {
  const undoStack = useRef<HistoryEntry[]>([]);
  const redoStack = useRef<HistoryEntry[]>([]);
  const lastCommittedHtml = useRef('');
  const applyingHistory = useRef(false);
  const composing = useRef(false);
  const programmaticDepth = useRef(0);
  const skipNextInput = useRef(false);
  const usedBeforeInput = useRef(false);
  const suppressHistoryUntilMs = useRef(0);

  const getDoc = () => iframeRef.current?.contentDocument ?? null;

  const commitLastCommittedFromDom = useCallback(() => {
    const doc = getDoc();
    if (doc?.body) lastCommittedHtml.current = doc.body.innerHTML;
  }, [iframeRef]);

  const pushUndoEntry = useCallback((html: string, sel: BodySelectionBookmark | null) => {
    undoStack.current.push({ html, sel });
    if (undoStack.current.length > MAX_UNDO_STEPS) undoStack.current.shift();
    redoStack.current = [];
  }, []);

  useEffect(() => {
    undoStack.current = [];
    redoStack.current = [];
    lastCommittedHtml.current = '';
  }, [reportContentKey]);

  const recordBeforeMutate = useCallback(() => {
    if (applyingHistory.current) return;
    const doc = getDoc();
    const win = iframeRef.current?.contentWindow;
    if (!doc?.body || !win) return;
    const sel = captureBodySelectionBookmark(win, doc.body);
    pushUndoEntry(lastCommittedHtml.current, sel);
  }, [iframeRef, pushUndoEntry]);

  const withProgrammatic = useCallback(
    (fn: () => void) => {
      const doc = getDoc();
      if (!doc?.body) return;
      programmaticDepth.current++;
      try {
        fn();
      } finally {
        programmaticDepth.current--;
        lastCommittedHtml.current = doc.body.innerHTML;
      }
    },
    [iframeRef],
  );

  const forceIframeHeightToContent = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) return;
    try {
      const h = getIframeDocumentContentHeightPx(doc);
      if (h > 0) iframe.style.height = `${h + 40}px`;
    } catch {
      /* ignore */
    }
  }, [iframeRef]);

  const tryUndo = useCallback((): boolean => {
    const doc = getDoc();
    const win = iframeRef.current?.contentWindow;
    if (!doc?.body || !win) return false;
    if (undoStack.current.length === 0) return false;
    applyingHistory.current = true;
    try {
      const curHtml = doc.body.innerHTML;
      const curSel = captureBodySelectionBookmark(win, doc.body);
      const prev = undoStack.current.pop();
      if (prev === undefined) return false;
      redoStack.current.push({ html: curHtml, sel: curSel });
      if (redoStack.current.length > MAX_UNDO_STEPS) redoStack.current.shift();
      doc.body.innerHTML = prev.html;
      lastCommittedHtml.current = prev.html;
      restoreBodySelectionBookmark(win, doc.body, prev.sel);
    } finally {
      applyingHistory.current = false;
    }
    suppressHistoryUntilMs.current = Date.now() + 220;
    win.focus();
    syncState();
    forceIframeHeightToContent();
    win.requestAnimationFrame(() => {
      forceIframeHeightToContent();
      if (iframeRef.current) scrollIframeCaretIntoHostView(iframeRef.current);
      win.requestAnimationFrame(() => {
        forceIframeHeightToContent();
        if (iframeRef.current) scrollIframeCaretIntoHostView(iframeRef.current);
      });
    });
    return true;
  }, [iframeRef, syncState, forceIframeHeightToContent]);

  const tryRedo = useCallback((): boolean => {
    const doc = getDoc();
    const win = iframeRef.current?.contentWindow;
    if (!doc?.body || !win) return false;
    if (redoStack.current.length === 0) return false;
    applyingHistory.current = true;
    try {
      const curHtml = doc.body.innerHTML;
      const curSel = captureBodySelectionBookmark(win, doc.body);
      const next = redoStack.current.pop();
      if (next === undefined) return false;
      undoStack.current.push({ html: curHtml, sel: curSel });
      if (undoStack.current.length > MAX_UNDO_STEPS) undoStack.current.shift();
      doc.body.innerHTML = next.html;
      lastCommittedHtml.current = next.html;
      restoreBodySelectionBookmark(win, doc.body, next.sel);
    } finally {
      applyingHistory.current = false;
    }
    suppressHistoryUntilMs.current = Date.now() + 220;
    win.focus();
    syncState();
    forceIframeHeightToContent();
    win.requestAnimationFrame(() => {
      forceIframeHeightToContent();
      if (iframeRef.current) scrollIframeCaretIntoHostView(iframeRef.current);
      win.requestAnimationFrame(() => {
        forceIframeHeightToContent();
        if (iframeRef.current) scrollIframeCaretIntoHostView(iframeRef.current);
      });
    });
    return true;
  }, [iframeRef, syncState, forceIframeHeightToContent]);

  const syncBaselineFromDom = useCallback(() => {
    commitLastCommittedFromDom();
  }, [commitLastCommittedFromDom]);

  const tryUndoRef = useRef(tryUndo);
  tryUndoRef.current = tryUndo;
  const tryRedoRef = useRef(tryRedo);
  tryRedoRef.current = tryRedo;

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    let detach: (() => void) | undefined;

    const attach = () => {
      detach?.();
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      if (!doc?.body || !win) return;

      undoStack.current = [];
      redoStack.current = [];
      lastCommittedHtml.current = doc.body.innerHTML;
      usedBeforeInput.current = false;

      const onCompositionStart = () => {
        if (applyingHistory.current || programmaticDepth.current > 0) return;
        if (isIframeChromeRefreshActive(win)) return;
        composing.current = true;
        const sel = captureBodySelectionBookmark(win, doc.body);
        pushUndoEntry(lastCommittedHtml.current, sel);
      };

      const onCompositionEnd = () => {
        composing.current = false;
        if (applyingHistory.current || programmaticDepth.current > 0) {
          commitLastCommittedFromDom();
          return;
        }
        lastCommittedHtml.current = doc.body.innerHTML;
        skipNextInput.current = true;
        usedBeforeInput.current = false;
      };

      const onBeforeInput = (e: Event) => {
        const ie = e as InputEvent;
        const it = ie.inputType;
        if (it === 'historyUndo' || it === 'historyRedo') {
          ie.preventDefault();
          return;
        }
        if (applyingHistory.current) return;
        if (composing.current) return;
        if (programmaticDepth.current > 0) return;
        if (isIframeChromeRefreshActive(win)) return;
        usedBeforeInput.current = true;
        const sel = captureBodySelectionBookmark(win, doc.body);
        pushUndoEntry(lastCommittedHtml.current, sel);
      };

      const onInput = (e: Event) => {
        const it = (e as InputEvent).inputType;
        if (it === 'historyUndo' || it === 'historyRedo') return;
        if (applyingHistory.current) return;
        if (composing.current) return;
        if (isIframeChromeRefreshActive(win)) {
          commitLastCommittedFromDom();
          usedBeforeInput.current = false;
          return;
        }
        if (skipNextInput.current) {
          skipNextInput.current = false;
          lastCommittedHtml.current = doc.body.innerHTML;
          usedBeforeInput.current = false;
          return;
        }
        if (programmaticDepth.current > 0) {
          lastCommittedHtml.current = doc.body.innerHTML;
          usedBeforeInput.current = false;
          return;
        }
        if (!usedBeforeInput.current) {
          if (Date.now() < suppressHistoryUntilMs.current) {
            lastCommittedHtml.current = doc.body.innerHTML;
            usedBeforeInput.current = false;
            return;
          }
          pushUndoEntry(lastCommittedHtml.current, null);
        }
        usedBeforeInput.current = false;
        lastCommittedHtml.current = doc.body.innerHTML;
      };

      const onKeyDown = (e: Event) => {
        const ke = e as KeyboardEvent;
        if (!(ke.metaKey || ke.ctrlKey) || ke.defaultPrevented) return;
        if (ke.key === 'z' && !ke.shiftKey) {
          if (tryUndoRef.current()) ke.preventDefault();
        } else if ((ke.key === 'z' && ke.shiftKey) || ke.key === 'y') {
          if (tryRedoRef.current()) ke.preventDefault();
        }
      };

      doc.addEventListener('compositionstart', onCompositionStart);
      doc.addEventListener('compositionend', onCompositionEnd);
      doc.addEventListener('beforeinput', onBeforeInput);
      doc.addEventListener('input', onInput);
      doc.addEventListener('keydown', onKeyDown, true);

      detach = () => {
        doc.removeEventListener('compositionstart', onCompositionStart);
        doc.removeEventListener('compositionend', onCompositionEnd);
        doc.removeEventListener('beforeinput', onBeforeInput);
        doc.removeEventListener('input', onInput);
        doc.removeEventListener('keydown', onKeyDown, true);
      };
    };

    const onLoad = () => attach();
    iframe.addEventListener('load', onLoad);
    if (iframe.contentDocument?.body) attach();

    return () => {
      iframe.removeEventListener('load', onLoad);
      detach?.();
    };
  }, [reportContentKey, iframeRef, commitLastCommittedFromDom, pushUndoEntry]);

  const isDirty = useCallback(() => undoStack.current.length > 0, []);

  return useMemo<IframeUndoHistoryApi>(
    () => ({
      recordBeforeMutate,
      withProgrammatic,
      tryUndo,
      tryRedo,
      syncBaselineFromDom,
      isDirty,
    }),
    [recordBeforeMutate, withProgrammatic, tryUndo, tryRedo, syncBaselineFromDom, isDirty],
  );
}
